<script setup lang="ts">
  import { ref, computed } from 'vue';
  import DOMPurify from 'dompurify';

  // Define props for the component
  const props = defineProps({
    loading: {
      type: Boolean,
      default: false,
    },
    cafes: {
      type: Array,
      default: () => [],
    },
    itemsPerPage: {
      type: Number,
      default: 24,
    },
    currentPage: {
      type: Number,
      default: 1,
    },
    totalPages: {
      type: Number,
      default: 1,
    },
    bannerPosition: {
      // Add this prop
      type: Number,
      default: 12, // Show banner after 6th item by default
    },
  });

  // Define emits to pass events back to parent
  const emit = defineEmits(['page-change']);

  // Computed property for visible page numbers in pagination
  const visiblePages = computed(() => {
    const pages = [];
    const maxVisible = 5;
    const startPage = Math.max(
      1,
      props.currentPage - Math.floor(maxVisible / 2)
    );
    const endPage = Math.min(props.totalPages, startPage + maxVisible - 1);

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }
    return pages;
  });

  const listContainer = ref(null);
  const { trackCafeClick } = useAnalytics()

  function handleCafeClick(cafe: any) {
    // Track cafe card click to measure discovery patterns and listing engagement
    trackCafeClick(cafe.name, cafe.id, cafe.category ?? cafe.type ?? '')
  }

  function changePage(page) {
    if (page >= 1 && page <= props.totalPages) {
      emit('page-change', page);
      // Scroll to top of the list container
      listContainer.value?.scrollIntoView({ behavior: 'smooth' });
    }
  };
  const sanitizeDescription = (description: string) => {
  return description ? DOMPurify.sanitize(description) : '';
};
</script>

<template>
  <div ref="listContainer">
    <div
      v-if="loading"
      class="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-3"
    >
      <div
        v-for="n in itemsPerPage"
        :key="n"
        class="rounded-md flex flex-col h-full pb-4 border overflow-hidden"
      >
        <div class="skeleton skeleton-image"></div>
        <div class="flex-1 flex-col px-4">
          <div class="skeleton skeleton-text"></div>
          <div class="skeleton skeleton-text"></div>
        </div>
      </div>
    </div>
    <div v-else>
      <div class="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4">
        <template v-for="(cafe, index) in cafes" :key="cafe.id">
          <li
            class="rounded-md flex flex-col h-full pb-4 border overflow-hidden"
          >
            <NuxtLink
              :to="`/cafe/${cafe.slug_name}`"
              class="flex flex-col h-full"
              @click="handleCafeClick(cafe)"
            >
              <NuxtImg
                :alt="`${cafe.name} - cafe di ${cafe.city}`"
                class="w-full h-48 object-cover mb-4"
                :src="cafe.photo"
                placeholder="/img/noimg.webp"
              />
              <div class="flex-1 flex flex-col px-4">
                <div class="flex-1">
                  <h2
                    class="text-lg text-gray-800 leading-tight line-clamp-2 font-semibold"
                  >
                    {{ cafe.name }}
                  </h2>
                  <p class="text-sm text-gray-500 line-clamp-2 mt-2" 
     v-html="sanitizeDescription(cafe.description)">
  </p>
                </div>

                <div class="flex justify-between mt-8">
                  <div class="flex items-center gap-1">
                    <img
                      src="/src/assets/img/city.svg"
                      alt="location"
                      class="h-3"
                    />
                    <p class="text-gray-500 text-xs">{{ cafe.city }}</p>
                  </div>
                  <div class="flex items-center gap-1 font-semibold">
                    <p class="text-gray-500 text-xs">{{ cafe.range }}</p>
                  </div>
                  <div class="flex items-center gap-1">
                    <img
                      src="/src/assets/img/rating.svg"
                      alt="star"
                      class="h-3"
                    />
                    <p class="text-gray-500 text-xs">{{ cafe.rating_num }}</p>
                  </div>
                </div>
              </div>
            </NuxtLink>
          </li>
          <!-- Add banner after specified position -->
        </template>
      </div>
      <div class="flex justify-center mt-4 space-x-2">
        <span
          v-if="currentPage > 1"
          @click="changePage(currentPage - 1)"
          class="cursor-pointer text-gray-500 hover:underline"
        >
          Previous
        </span>
        <span
          v-for="page in visiblePages"
          :key="page"
          @click="changePage(page)"
          :class="{
            'font-bold text-gray-800': currentPage === page,
            'text-gray-700': currentPage !== page,
          }"
          class="cursor-pointer hover:underline"
        >
          {{ page }}
        </span>
        <span
          v-if="currentPage < totalPages"
          @click="changePage(currentPage + 1)"
          class="cursor-pointer text-gray-800 hover:underline"
        >
          Next
        </span>
        <span
          v-if="currentPage < totalPages && !visiblePages.includes(totalPages)"
          @click="changePage(totalPages)"
          class="cursor-pointer text-gray-800 hover:underline"
        >
          Last ({{ totalPages }})
        </span>
      </div>
    </div>
  </div>
</template>

<style scoped>
  /* Skeleton loading styles */
  .skeleton {
    background-color: #e0e0e0;
    border-radius: 4px;
    animation: pulse 1.5s infinite ease-in-out;
  }

  @keyframes pulse {
    0% {
      opacity: 1;
    }
    50% {
      opacity: 0.4;
    }
    100% {
      opacity: 1;
    }
  }

  .skeleton-text {
    height: 1em;
    margin-bottom: 0.5em;
    width: 80%;
  }

  .skeleton-image {
    height: 150px;
    width: 100%;
    margin-bottom: 1em;
  }
</style>
